/**
 * Department language settings for admins (D2, workspace docs/guardian/03 §3.3,
 * 2026-10-22): whether Guardian answers in plain language by default, and
 * whether it adapts to each member's own style. GET is any member; PUT is
 * admin. A 503 (`terms_unavailable`/`style_unavailable`) hides the section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { fetchDepartmentSettings, updateDepartmentSettings } from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

const OFF_CODES = new Set(['terms_unavailable', 'style_unavailable', 'settings_unavailable']);

export default function DepartmentSettings({ authToken }) {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const [saving, setSaving] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    const res = await fetchDepartmentSettings(authToken);
    if (!alive.current) return;
    if (res?.success) {
      setSettings({
        plainLanguageDefault: Boolean(res.data?.plainLanguageDefault),
        styleMirroring: Boolean(res.data?.styleMirroring),
      });
    } else if (OFF_CODES.has(res?.data?.code)) {
      setOff(true);
    } else {
      setError(departmentErrorMessage(res, 'Could not load department settings.'));
    }
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const update = async (changes) => {
    setSaving(true);
    setError(null);
    const prev = settings;
    setSettings((s) => ({ ...s, ...changes }));
    const res = await updateDepartmentSettings(authToken, changes);
    setSaving(false);
    if (!res?.success) {
      setSettings(prev); // roll back the optimistic flip on refusal
      setError(departmentErrorMessage(res, 'Could not save the setting.'));
    } else if (res.data) {
      setSettings({
        plainLanguageDefault: Boolean(res.data.plainLanguageDefault),
        styleMirroring: Boolean(res.data.styleMirroring),
      });
    }
  };

  if (off) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="h6" component="h2">How Guardian answers</Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {settings && (
        <>
          <FormControlLabel
            control={(
              <Switch
                checked={settings.plainLanguageDefault}
                disabled={saving}
                onChange={(e) => update({ plainLanguageDefault: e.target.checked })}
                inputProps={{ 'aria-label': 'Plain language by default' }}
              />
            )}
            label="Plain language by default"
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -1 }}>
            Members start with answers in everyday words instead of codes. Each member can still turn it off per question.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Switch
                checked={settings.styleMirroring}
                disabled={saving}
                onChange={(e) => update({ styleMirroring: e.target.checked })}
                inputProps={{ 'aria-label': "Answers match each member's style" }}
              />
            )}
            label="Answers match each member's style"
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -1 }}>
            Guardian learns how each member likes answers written (length, bullets or prose, units) and matches it.
          </Typography>
        </>
      )}
    </Paper>
  );
}
